import {
  StapleTableHeader,
  StapleTableCell,
  StapleTableHeading,
  StapleTableRow,
  StapleTableBody,
} from '@insurancemenu/imenu-components-lib';
import {
  EmployeeInvitationStatusTypes,
  SubscriberProgress,
  SubscribersSortBy,
  SubscribersSortOrder,
} from 'types/electionService';
import SubscriberStatus from 'components/common/election/SubscriberStatus';
import { observer } from 'mobx-react-lite';
import React, { useEffect } from 'react';
import { useMonitorAddEmployeesPageStore } from 'store/pages/rfq/monitor-add-employees/pageStore';
import SubscriberProgressTag from 'components/common/add-employees/SubscriberProgressTag';
import { AvailableActions } from 'components/common/add-employees/AvailableActions';
import { SendInvitationModal } from 'components/common/add-employees/SendInvitationModal';
import { ActionsUiStores, EmployeeActionUiPresenter } from 'store/modules/add-employees/employeeDelegate';
import WarningModals from 'components/common/add-employees/WarningModals';
import { DependentsQuantity } from 'components/common/add-employees/DependentsQuantity';
import { InviteEmployeeAction } from 'store/modules/add-employees/types';
import { Mail } from '@insurancemenu/imenu-components-lib/dist/icons';
import { ActionCellStyled, StapleTableStyled } from './styles';
import { ADD_EMPLOYEES_TABLE_TEST_IDS } from '../constants';

interface EmployeeEntry {
  name: string;
  selfEnrollStatus: EmployeeInvitationStatusTypes | null;
  imqsStatus: SubscriberProgress;
  email?: string;
  dependentsNumber: number;
  actions: EmployeeActionUiPresenter[];
  actionsUiStores: ActionsUiStores;
}

export const rfqAddEmployeesActionIcons: Partial<Record<string, React.FC>> = {
  [InviteEmployeeAction.SEND_INVITATION]: Mail,
  [InviteEmployeeAction.RESEND_INVITATION]: Mail,
};

const AddEmployeesTableRow: React.FC<EmployeeEntry> = observer(
  ({ name, selfEnrollStatus, imqsStatus, email, dependentsNumber, actions, actionsUiStores }) => {
    return (
      <StapleTableRow data-test-id={ADD_EMPLOYEES_TABLE_TEST_IDS.TABLE_ROW}>
        <StapleTableCell width="8rem">
          {name}
          <DependentsQuantity dependentsNumber={dependentsNumber} />
        </StapleTableCell>
        <StapleTableCell width="10rem">{email}</StapleTableCell>
        <StapleTableCell width="6rem">
          {selfEnrollStatus && <SubscriberStatus status={selfEnrollStatus} />}
        </StapleTableCell>
        <StapleTableCell width="6rem">
          <SubscriberProgressTag status={imqsStatus} />
        </StapleTableCell>
        <ActionCellStyled width="6rem">
          <AvailableActions
            availableActions={actions}
            warningModals={actionsUiStores.warningModalDelegates}
            actionIcons={rfqAddEmployeesActionIcons}
          />
          {actionsUiStores.sendInvitationModalStore && (
            <SendInvitationModal modalStore={actionsUiStores.sendInvitationModalStore} />
          )}
          <WarningModals availableActions={actions} warningModals={actionsUiStores.warningModalDelegates} />
        </ActionCellStyled>
      </StapleTableRow>
    );
  },
);

export const AddEmployeesTable: React.FC = observer(() => {
  const { getEmployees, paginationDelegate, employees, isLoading } = useMonitorAddEmployeesPageStore();

  const onEmployeeNameSort = (sortKey: string, sortingOrder: 'ASC' | 'DESC'): void => {
    paginationDelegate.setSortBy(sortKey as SubscribersSortBy);
    paginationDelegate.setSortOrder(sortingOrder as SubscribersSortOrder);
    paginationDelegate.setPage(1);
  };

  useEffect(() => {
    void getEmployees();
  }, [
    paginationDelegate.page,
    paginationDelegate.sortBy,
    paginationDelegate.sortOrder,
    paginationDelegate.filters.employeeName,
    paginationDelegate.filters.status,
    paginationDelegate.filters.subProgress,
  ]);

  return (
    <StapleTableStyled
      data-test-id={ADD_EMPLOYEES_TABLE_TEST_IDS.TABLE}
      totalElements={paginationDelegate.totalElements}
      loading={isLoading}
      pagination={{
        tablePage: paginationDelegate.page,
        minPage: 1,
        maxPage: paginationDelegate.totalPages,
        onPageChange: (page) => {
          paginationDelegate.setPage(page);
        },
      }}
    >
      <StapleTableHeader>
        <StapleTableRow>
          <StapleTableHeading
            width="8rem"
            sorting
            sortingKey={SubscribersSortBy.NAME}
            onSortCallback={onEmployeeNameSort}
          >
            Employee Name
          </StapleTableHeading>
          <StapleTableHeading width="10rem">Email Address</StapleTableHeading>
          <StapleTableHeading width="6rem">Status</StapleTableHeading>
          <StapleTableHeading width="6rem">IMQs Progress</StapleTableHeading>
          <StapleTableHeading width="6rem" />
        </StapleTableRow>
      </StapleTableHeader>
      <StapleTableBody>
        {employees.map(({ rawData, fullName, dependentsNumber, availableActions, actionsUiStores }) => (
          <AddEmployeesTableRow
            key={rawData.id}
            name={fullName}
            imqsStatus={rawData.progress}
            email={rawData.email}
            selfEnrollStatus={rawData.selfEnrollStatus}
            dependentsNumber={dependentsNumber}
            actions={availableActions}
            actionsUiStores={actionsUiStores}
          />
        ))}
      </StapleTableBody>
    </StapleTableStyled>
  );
});
